import React from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../AppRootProviders";
import { LoginPage } from "../../features/auth";
import ChangePasswordPage from "../../features/auth/reset-password/ResetPasswordPage";
import GlobalLayout from "../../features/layout/GlobalLayout";
import QRScannerLayout from "../../features/qr-scanner/QRScannerLayout";
import {
  isAccountDeletionPath,
  isAnonymousPublicPath,
  isPasswordResetPath,
  isQRScannerPath,
} from "./appRouteMatchers";
import AuthenticatedProfileBootstrap from "../../features/profile/components/AuthenticatedProfileBootstrap";
import AdminRouteLayout from "./layouts/AdminRouteLayout";
import MerchantRouteLayout from "./layouts/MerchantRouteLayout";
import { adminRoutes } from "./routes/adminRoutes";
import { merchantRoutes } from "./routes/merchantRoutes";
import { passwordRoutes } from "./routes/passwordRoutes";
import { publicRoutes } from "./routes/publicRoutes";
import { qrScannerRoutes } from "./routes/qrScannerRoutes";

const LoadingScreen = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-primary mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-300">{t("common.loading")}</p>
      </div>
    </div>
  );
};

const AppRoutesComponent: React.FC = () => {
  const { isInitialized, isLoggedIn } = useAppContext();
  const { pathname, search } = useLocation();

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  const appShell = (content: React.ReactNode) => <GlobalLayout>{content}</GlobalLayout>;

  if (isPasswordResetPath(pathname) || isAccountDeletionPath(pathname)) {
    return appShell(<Routes>{passwordRoutes}</Routes>);
  }

  if (isAnonymousPublicPath(pathname, search)) {
    return appShell(<Routes>{publicRoutes}</Routes>);
  }

  if (!isLoggedIn) {
    if (isQRScannerPath(pathname)) {
      return appShell(
        <Routes>
          <Route
            path="/qr-scanner/*"
            element={<QRScannerLayout />}
          >
            {qrScannerRoutes}
          </Route>
        </Routes>,
      );
    }

    return appShell(<LoginPage />);
  }

  return (
    <AuthenticatedProfileBootstrap>
      <GlobalLayout>
        <Routes>
          <Route
            path="/"
            element={<LoginPage />}
          />
          <Route
            path="reset-password"
            element={<ChangePasswordPage />}
          />
          <Route
            path="/qr-scanner/*"
            element={<QRScannerLayout />}
          >
            {qrScannerRoutes}
          </Route>
          <Route
            path="/merchant"
            element={<MerchantRouteLayout />}
          >
            {merchantRoutes}
          </Route>
          <Route
            path="/*"
            element={<AdminRouteLayout />}
          >
            {adminRoutes}
          </Route>
        </Routes>
      </GlobalLayout>
    </AuthenticatedProfileBootstrap>
  );
};

export default AppRoutesComponent;
